import React from "react";
import { Link } from "react-router-dom";
import "./MembershipStatus.css";

// Shared look for the member screens on /membership (active, trial, payment
// problem, cancelled), in the Momentum brand. These components only lay things
// out: every button gets its handler from MembershipPage, so billing and
// Stripe behaviour stay there.

export function MemberScreen({ tone = "ok", badge, title, intro, perk, links, children }) {
  return (
    <div className="ms-page">
      <Link to="/" className="ms-home">← Go Home</Link>
      <header className="ms-head">
        <span className={`ms-badge ${tone}`}>{badge}</span>
        <h1 className="ms-display">{title}</h1>
        {intro && <p className="ms-intro">{intro}</p>}
      </header>
      {perk}
      {links}
      <section className="ms-card" aria-label="Your membership details">
        <p className="ms-label">Your membership details</p>
        {children}
      </section>
    </div>
  );
}

// chips: [{ tone: "ok" | "warn" | "plan", label }]
// dates: [{ label, value }] where value is a Date or null
export function MemberDetails({ chips = [], dates = [], fallback = "Unavailable" }) {
  return (
    <>
      <div className="ms-chips">
        {chips.filter(Boolean).map((chip) => (
          <span key={chip.label} className={`ms-chip ${chip.tone || "plan"}`}>
            <ChipIcon tone={chip.tone} />
            {chip.label}
          </span>
        ))}
      </div>
      <dl className={`ms-dates n${dates.length}`}>
        {dates.map((date) => (
          <div key={date.label} className="ms-date">
            <dt>{date.label}</dt>
            <dd>{date.value ? date.value.toLocaleDateString() : date.fallback || fallback}</dd>
          </div>
        ))}
      </dl>
    </>
  );
}

export function MemberPerkCard({ freeMembersChallenge }) {
  return (
    <div className="ms-perk">
      <p className="ms-perk-label">Member perk</p>
      <p className="ms-perk-text">
        This month's Momentum Members Challenge is the "{freeMembersChallenge.name}"
      </p>
      <a
        href={freeMembersChallenge.url}
        target="_blank"
        rel="noopener noreferrer"
        className="ms-btn"
      >
        Claim it here →
      </a>
    </div>
  );
}

const QUICK_LINKS = [
  { to: "/training-plans", icon: "plans", title: "Training plans", body: "5K to marathon plans and a pace calculator." },
  { to: "/strong-healthy", icon: "strength", title: "Strong & Healthy hub", body: "Strength, mobility and nutrition programs." },
  { to: "/strong-healthy/monthly-meetups", icon: "meetups", title: "Monthly meetups", body: "Recordings and show notes from each meetup." },
  { to: "/streaks", icon: "streaks", title: "Streaks", body: "Track your active days and keep the streak going." },
];

export function MemberQuickLinks() {
  return (
    <section className="ms-links" aria-labelledby="ms-links-heading">
      <p id="ms-links-heading" className="ms-label">Explore your membership</p>
      <ul>
        {QUICK_LINKS.map((link, index) => (
          <li key={link.to} className={`f${index}`}>
            <Link to={link.to}>
              <span className="ms-licon"><LinkIcon name={link.icon} /></span>
              <b>{link.title}</b>
              <span>{link.body}</span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function CancelMembershipModal({ periodEnd, onConfirmCancel }) {
  const membershipEndsLabel = periodEnd ? periodEnd.toLocaleDateString() : null;

  return (
    <div className="ms-modal">
      <div className="ms-modal-head">
        <span className="ms-badge warn">Before you cancel</span>
        <h3>Here’s what you’ll lose if you cancel</h3>
        <p>
          {membershipEndsLabel
            ? `Your membership will stay active until ${membershipEndsLabel}, then these member benefits will end.`
            : "Your member benefits will end when your current billing period finishes."}
        </p>
      </div>

      <ul className="ms-lose">
        <li>Premium challenge access each quarter</li>
        <li>Members-only challenges each quarter</li>
        <li>Running training plans</li>
        <li>Strength and mobility programs</li>
        <li>The member community and accountability support</li>
        <li>Future member perks, bonus offers, and special registrations</li>
      </ul>

      <div className="ms-modal-actions">
        <button
          type="button"
          data-close
          onClick={onConfirmCancel}
          className="ms-btn-ghost"
        >
          I want to cancel
        </button>
        <button
          type="button"
          data-close
          className="ms-btn"
        >
          Forget about it, I want to stay
        </button>
      </div>
    </div>
  );
}

function ChipIcon({ tone }) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {tone === "ok" && <path d="m5 12 5 5 9-10" />}
      {tone === "warn" && <><path d="M12 8v5" /><path d="M12 16.5v.01" /><circle cx="12" cy="12" r="9" /></>}
      {(!tone || tone === "plan") && <><rect x="3" y="6" width="18" height="13" rx="2" /><path d="M3 10h18" /></>}
    </svg>
  );
}

function LinkIcon({ name }) {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {name === "plans" && <><rect x="5" y="4" width="14" height="17" rx="2" /><path d="M9 4V3h6v1M9 10h6M9 14h6M9 18h3" /></>}
      {name === "strength" && <path d="M6 7v10M3 9.5v5M18 7v10M21 9.5v5M6 12h12" />}
      {name === "meetups" && <><rect x="3" y="6" width="13" height="12" rx="2" /><path d="m16 10 5-3v10l-5-3" /></>}
      {name === "streaks" && <path d="M12 3c1 3.5 5 5.5 5 10a5 5 0 0 1-10 0c0-2.2 1-3.6 2-4.6.3 1.6 1 2.6 2 3.1C11 9 11 6 12 3Z" />}
    </svg>
  );
}
